import React, { FunctionComponent, Fragment, useCallback, useState, useContext } from "react";
import { Grid, Theme, makeStyles, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { LoginFormTextField } from "./form-components/login-form-textfield";
import { LoginFormButton } from "./form-components/login-form-button";
import { usePassword } from "../../hooks/use-password";
import { useEmail } from "../../hooks/use-email";
import { RegisterFormTooltipTitle } from "./register-form-tooltip-title";
import { useRegisterMutation } from "../../api/smartmelts-api/graphql/generated";
import { useGraphQL } from "../../api/context/graphql-context";
import { LoginContext } from "./context/login-context";
import { LOGIN_FORM_TYPE } from "../../models/login-form-type";
import { getGraphqlErrorLocalized } from "../../api/errors/graphql-error-handler";
import { SnackbarSeverity, useSnackbar } from "../../components/snackbar/snackbar-context";
import { useEnterCallback } from "../../hooks/use-enter-callback";

const useStyles = makeStyles((theme: Theme) => ({
  tooltip: {
    padding: 0,
    backgroundColor: "transparent",
  },
}));

interface IRegisterFormProps {}

export const RegisterForm: FunctionComponent<IRegisterFormProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { setAuthorizationHeader } = useGraphQL();
  const { showSnackbar } = useSnackbar();
  const {
    setFormType,
    toggleShakeAnimation,
    onEmailChange: onLoginEmailChange,
    onPasswordChange: onLoginPasswordChange,
  } = useContext(LoginContext);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const { email, isEmailValid, onEmailChange } = useEmail("");
  const { password, isPasswordValid, onPasswordChange } = usePassword("");
  const {
    password: passwordRepeat,
    isPasswordValid: isPasswordRepeatValid,
    onPasswordChange: onPasswordRepeatChange,
    isPasswordSame,
  } = usePassword("", password);

  const onFirstNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value),
    [setFirstName],
  );
  const onLastNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setLastName(event.target.value),
    [setLastName],
  );
  const customOnEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onEmailChange(event.target.value),
    [onEmailChange],
  );
  const customOnPasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onPasswordChange(event.target.value),
    [onPasswordChange],
  );
  const customOnPasswordRepeatChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onPasswordRepeatChange(event.target.value),
    [onPasswordRepeatChange],
  );

  const [registerMutation, { loading }] = useRegisterMutation({
    onCompleted: (data) => {
      setAuthorizationHeader(data.register.token);
      setFormType(LOGIN_FORM_TYPE.CODE);
      onLoginEmailChange(email);
      onLoginPasswordChange(password);
    },
    onError: (error) => {
      toggleShakeAnimation();
      showSnackbar(getGraphqlErrorLocalized(t, "login", error), SnackbarSeverity.ERROR);
    },
  });

  const onRegister = useCallback(() => {
    if (!!firstName && !!lastName && isEmailValid && isPasswordRepeatValid && isPasswordSame && isPasswordValid) {
      registerMutation({
        variables: {
          email,
          firstName,
          lastName,
          password,
        },
      });
    } else {
      toggleShakeAnimation();
    }
  }, [
    email,
    firstName,
    lastName,
    password,
    isPasswordValid,
    isPasswordRepeatValid,
    isPasswordSame,
    isEmailValid,
    registerMutation,
    toggleShakeAnimation,
  ]);

  const onKeyDown = useEnterCallback(onRegister);

  return (
    <Fragment>
      <Tooltip title={<RegisterFormTooltipTitle />} placement="top" classes={{ tooltip: classes.tooltip }}>
        <Grid container direction="column">
          <LoginFormTextField
            label={t("login.fields.email")}
            value={email}
            onChange={customOnEmailChange}
            error={email.length !== 0 && !isEmailValid}
            errorMessage={t("login.errors.email_invalid")}
            onKeyDown={onKeyDown}
          />
          <Grid container>
            <Grid item xs={12} sm={6}>
              <LoginFormTextField
                label={t("login.fields.firstname")}
                onChange={onFirstNameChange}
                value={firstName}
                onKeyDown={onKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LoginFormTextField
                label={t("login.fields.lastname")}
                onChange={onLastNameChange}
                value={lastName}
                onKeyDown={onKeyDown}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <LoginFormTextField
                label={t("login.fields.password")}
                type="password"
                value={password}
                onChange={customOnPasswordChange}
                error={password.length !== 0 && !isPasswordValid}
                errorMessage={t("login.errors.password_too_short")}
                onKeyDown={onKeyDown}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LoginFormTextField
                label={t("login.fields.repeat_password")}
                type="password"
                value={passwordRepeat}
                onChange={customOnPasswordRepeatChange}
                error={passwordRepeat.length !== 0 && (!isPasswordRepeatValid || !isPasswordSame)}
                errorMessage={
                  !isPasswordSame ? t("login.errors.passwords_dont_match") : t("login.errors.password_too_short")
                }
                onKeyDown={onKeyDown}
              />
            </Grid>
          </Grid>
        </Grid>
      </Tooltip>
      <Grid container justify="center">
        <LoginFormButton onClick={onRegister} loading={loading}>
          {t("login.sign_up")}
        </LoginFormButton>
      </Grid>
    </Fragment>
  );
};
